import { Paper, Stack, Text, Group, Alert, Badge, Divider, Button } from '@mantine/core';
import {
  IconCheck,
  IconAlertTriangle,
  IconFileImport,
  IconBolt,
  IconCalculator,
  IconSparkles,
  IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { ImportHourlyDataResult } from '../../hooks/useImportHourlyData';

export interface ImportSummaryProps {
  result: ImportHourlyDataResult;
}

const MONTH_NAMES = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
];

function formatMonthsList(months: number[]): string {
  if (months.length === 0) return 'brak';
  if (months.length === 1) return MONTH_NAMES[months[0] - 1];
  if (months.length === 12) return 'wszystkie miesiące';

  const sorted = [...months].sort((a, b) => a - b);
  const monthNames = sorted.map((m) => MONTH_NAMES[m - 1]);

  if (monthNames.length <= 3) {
    return monthNames.join(', ');
  }

  return `${monthNames.slice(0, 2).join(', ')} i ${monthNames.length - 2} inne`;
}

export function ImportSummary({ result }: ImportSummaryProps) {
  const { summary, importedMonths, extrapolatedMonths, warnings } = result;
  const navigate = useNavigate();

  if (!summary) {
    return (
      <Alert color="blue" icon={<IconCheck size={18} />}>
        Import zakończony pomyślnie!
      </Alert>
    );
  }

  return (
    <Paper p="lg" withBorder radius="md" bg="green.0">
      <Stack gap="md">
        {/* Success Header */}
        <Group gap="xs">
          <IconCheck size={24} color="var(--mantine-color-green-6)" />
          <Text fw={600} size="lg" c="green">
            Import zakończony pomyślnie!
          </Text>
        </Group>

        <Divider />

        {/* Import Statistics */}
        <Stack gap="sm">
          <Group gap="xs">
            <IconFileImport size={18} />
            <Text size="sm" fw={500}>
              Podsumowanie importu
            </Text>
          </Group>

          <Group gap="md" grow>
            <Paper p="sm" withBorder bg="white">
              <Text size="xs" c="dimmed" mb={4}>
                Zaimportowano
              </Text>
              <Text size="lg" fw={600} c="blue">
                {result.importedHoursCount.toLocaleString('pl-PL')} godz.
              </Text>
              <Text size="xs" c="dimmed">
                {formatMonthsList(importedMonths)}
              </Text>
            </Paper>

            <Paper p="sm" withBorder bg="white">
              <Text size="xs" c="dimmed" mb={4}>
                Oszacowano
              </Text>
              <Text size="lg" fw={600} c="grape">
                {result.extrapolatedHoursCount.toLocaleString('pl-PL')} godz.
              </Text>
              <Text size="xs" c="dimmed">
                {extrapolatedMonths.length} miesięcy
              </Text>
            </Paper>
          </Group>
        </Stack>

        <Divider />

        {/* Consumption Summary */}
        <Stack gap="sm">
          <Group gap="xs">
            <IconBolt size={18} />
            <Text size="sm" fw={500}>
              Zużycie energii
            </Text>
          </Group>

          <Group gap="md" grow>
            <Paper p="sm" withBorder bg="white">
              <Text size="xs" c="dimmed" mb={4}>
                Całkowite zużycie roczne
              </Text>
              <Text size="xl" fw={700} c="dark">
                {summary.totalConsumption.toLocaleString('pl-PL', {
                  maximumFractionDigits: 0,
                })}{' '}
                kWh
              </Text>
            </Paper>

            <Paper p="sm" withBorder bg="white">
              <Text size="xs" c="dimmed" mb={4}>
                Średnie dzienne
              </Text>
              <Text size="xl" fw={700} c="dark">
                {summary.avgDailyConsumption.toLocaleString('pl-PL', {
                  maximumFractionDigits: 2,
                })}{' '}
                kWh/dzień
              </Text>
            </Paper>
          </Group>
        </Stack>

        <Divider />

        {/* Next Steps */}
        <Stack gap="sm">
          <Alert color="green" icon={<IconCheck size={18} />} title="Dane zostały zapisane!">
            <Text size="sm" mb="md">
              Twoje dane godzinowe zostały pomyślnie zaimportowane i zapisane w systemie.
              Możesz teraz wykorzystać je w kalkulatorach do dokładniejszych porównań taryf.
            </Text>
          </Alert>

          <Text size="sm" fw={500}>
            Co możesz teraz zrobić?
          </Text>

          <Group grow>
            <Button
              variant="filled"
              color="blue"
              size="md"
              leftSection={<IconCalculator size={20} />}
              rightSection={<IconArrowRight size={16} />}
              onClick={() => navigate('/calculator')}
            >
              Kalkulator Szczegółowy
            </Button>
            <Button
              variant="filled"
              color="violet"
              size="md"
              leftSection={<IconSparkles size={20} />}
              rightSection={<IconArrowRight size={16} />}
              onClick={() => navigate('/pro-calculator')}
            >
              Kalkulator Profesjonalny
            </Button>
          </Group>

          <Alert color="blue" variant="light">
            <Stack gap="sm">
              <Text size="sm">
                <strong>Wskazówka:</strong> W kalkulatorach wybierz opcję wzorca zużycia
                <strong> "Z raportu Tauron"</strong> aby wykorzystać zaimportowane dane godzinowe.
              </Text>
              <Group gap="xs">
                <Button
                  component="a"
                  href="/calculator"
                  size="xs"
                  variant="light"
                  color="blue"
                >
                  Kalkulator Szczegółowy
                </Button>
                <Button
                  component="a"
                  href="/pro-calculator"
                  size="xs"
                  variant="light"
                  color="violet"
                >
                  Kalkulator Profesjonalny
                </Button>
              </Group>
            </Stack>
          </Alert>
        </Stack>

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <>
            <Divider />
            <Alert color="yellow" icon={<IconAlertTriangle size={18} />} title="Ostrzeżenia">
              <Stack gap="xs">
                {warnings.map((warning, index) => (
                  <Group key={index} gap="xs">
                    <Badge size="sm" color="yellow" variant="light">
                      {index + 1}
                    </Badge>
                    <Text size="sm">{warning}</Text>
                  </Group>
                ))}
              </Stack>
            </Alert>
          </>
        )}
      </Stack>
    </Paper>
  );
}
